import React, {useEffect, useRef, useState, useMemo} from 'react';
import md5 from 'md5';
import {Checkbox, Spin} from 'antd';
import {observer} from 'mobx-react';
import {useStore} from '../../../stores';
import {getScriptDetail, getVirtualCameraConfig, setLivePushStop, getGMVStartByPlugin,
    stopGetGMVListByPlugin, getSign, batchCheckLogin, batchCheckPluginLoginRes, goodsCardByPlugin, getLiveInfoByPlugin
} from '../../../services';
import {SOURCEMAP, TASKTYPEMAP, ACCOUNTTYPEMAP, COMMENTKINDMAP} from '../../../config';
import {useInterval} from '../../../../../utils/extHooks';
import iconEmpty from '../../../assets/images/empty.png';
import iconRefresh from '../../../assets/images/icon_ppt_image_change.png';
import iconStopExternalLive from '../../../assets/images/icon_stop_external_live.png';
import iconAnchor from '../../../assets/images/icon_anchor.png';
import iconRightArrow from '../../../assets/images/icon_character_right_arrow.png';
import iconPlaying from '../../../assets/voice/playing.gif';
import FlvPlayer from './FlvPlayer';
import './index.less';

const ExternalLive = (props) => {
    const store = useStore();
    const {} = props;
    const [commentList, setCommentList] = useState([]);
    const [gmvData, setGmvData] = useState({});
    const [scriptInfo, setScriptInfo] = useState({});
    const [wsIdCount, setWsIdCount] = useState(1);
    const [liveIsAlive, setLiveIsAlive] = useState(false);
    const [pollingTask, setPollingTask] = useState('');
    const [hasAuth, setHasAuth] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [getGmvIng, setGetGmvIng] = useState(false);

    const huiboWs = useRef(null);
    const heartWs = useRef(null);
    const pluginWs = useRef(null);

    const source = useMemo(() => {
        const cuInfo = store.externalStore.curLoginAccount;
        return SOURCEMAP.find(s => s.value === cuInfo?.platform)?.label || '';
    }, [store.externalStore.curLoginAccount]);

    useEffect(() => {
        const cuInfo = store.externalStore.curLoginAccount;
        if (cuInfo?.platform === 5) {
            setHasAuth(true);
            setAuthLoading(false);
        }
        else {
            if (store.externalStore.pollingCheckLoginTask) {
                setPollingTask(store.externalStore.pollingCheckLoginTask);
            }
            else {
                if (cuInfo?.platform) {
                    checkListPluginStatus([cuInfo]);
                }
                else {
                    setHasAuth(false);
                    setAuthLoading(false);
                }
            }
        }
        getScriptInfo();
        return () => {
            pluginGMVEnd();
        };
    }, [store.externalStore.curLoginAccount]);

    useEffect(() => {
        if (hasAuth && store.externalLiveInfo?.id) {
            heartbeatWsStart();
        }
    }, [hasAuth]);

    useEffect(() => {
        if (liveIsAlive) {
            getWsData();
        }
    }, [liveIsAlive]);

    const checkListPluginStatus = async (list) => {
        if (!list || !list.length) {
            return;
        }

        const res = await batchCheckLogin(list);

        if (res?.code === 0 && res?.data) {
            setPollingTask(res.data.taskId);
        }
        else {
            setHasAuth(false);
            setAuthLoading(false);
        }
    };

    const pollingBatchCheck = async () => {
        const res = await batchCheckPluginLoginRes(pollingTask).catch(e => {});

        if (res?.code === 0 && res?.data?.taskStatus !== 403) {
            setPollingTask('');

            if (res?.data?.taskStatus === 0 && res?.data?.list.length) {
                setHasAuth(true);
                setAuthLoading(false);
            }
            else {
                setHasAuth(false);
                setAuthLoading(false);
            }
        }
    };

    useInterval(pollingBatchCheck, pollingTask ? 3000 : null);

    useInterval(async () => {
        if (heartWs.current) {
            const sign = await getSign();
            heartWs.current.send(JSON.stringify({
                taskType: TASKTYPEMAP[source],
                userId: window.huibo_external_auth_uk,
                accountType: ACCOUNTTYPEMAP[source],
                action: 'HEARTBEAT',
                requestPlat: 'HUI_BO_XING',
                forceLogin: true,
                timeStamp: sign.ts,
                sign
            }));
        }
        if (huiboWs.current) { // huiboWs保活
            huiboWs.current.send('');
        }
    }, hasAuth ? 5000 : null);

    useInterval(async () => {
        if (huiboWs.current) {
            const res = await getLiveInfoByPlugin(source, {
                platuserId: store.externalStore.curLoginAccount?.third_id
            });
            const liveInfo = (res.data.liveList || [])[0];
            if (res.code === 0 && liveInfo) {
                huiboWs.current.send(JSON.stringify({id: wsIdCount, path: 'live.choose', data: {
                    room_id: liveInfo.id,
                    start_t: Number(liveInfo.beginTime)
                }}));
                setWsIdCount((prev) => {
                    return prev + 1;
                });
                setScriptInfo({
                    ...scriptInfo,
                    third_url: liveInfo.id,
                    start_t: Number(liveInfo.beginTime)
                });
            }
        }
    }, getGmvIng && !scriptInfo.third_url ? 10000 : null);

    const getScriptInfo = async () => {
        const scriptId = store.externalLiveInfo?.script_id;
        let info = {};
        const {errno: errno1, data: data1} = await getScriptDetail({
            script_id: scriptId,
            script_type: store.customerAcquisitionAuth ? 2 : 1 // 剧本类型，获客2，带货,本地生活1
        });
        if (errno1 === 0) {
            info = data1;
        }
        const {errno: errno2, data: data2} = await getVirtualCameraConfig({// 获取拉流地址
            script_id: scriptId
        });
        if (+errno2 === 0) {
            info = {
                ...store.externalLiveInfo,
                ...info,
                ...data2
            };
        }
        setScriptInfo(info);
    };

    const clearWs = () => {
        if (store.externalStore.curLoginAccount?.platform === 5) {
            huiboWs.current?.close();
            huiboWs.current = null;
        }
        else {
            huiboWs.current?.close();
            huiboWs.current = null;
            pluginWs.current?.close();
            pluginWs.current = null;
        }
    };

    const getWsData = async () => {
        clearWs();
        setCommentList([]);
        if (liveIsAlive) {
            if (store.externalStore.curLoginAccount?.platform === 5) {
                huiboWsStart();
            }
            else {
                await pluginGMVStart();
                pluginWsStart();
                huiboWsStart();
            }
        }
    };
    const pluginGMVStart = async () => {
        const res = await getGMVStartByPlugin(source, {
            platuserId: store.externalStore.curLoginAccount?.third_id
        });
        if (res.code === 0) {
            setGetGmvIng(true);
        }
    };

    const pluginGMVEnd = async () => {
        const res = await stopGetGMVListByPlugin(source, {
            platuserId: store.externalStore.curLoginAccount?.third_id
        });
        if (res.code === 0) {
            setGetGmvIng(false);
        }
    };

    const huiboWsStart = async () => {
        const liveInfo = store.externalLiveInfo;
        const cuInfo = store.externalStore.curLoginAccount;
        huiboWs.current = new WebSocket(
            `wss://${window.location.host}/ai_anchor/open/xl/dashboard/ws?open_id=${cuInfo.third_id}&platform=${cuInfo.platform}&room_id=${liveInfo.room_id}&need_qa=${liveInfo.need_qa}`
        );

        huiboWs.current.onopen = e => {
            if (scriptInfo.third_url) {
                huiboWs.current.send(JSON.stringify({id: wsIdCount, path: 'live.choose', data: {
                    room_id: scriptInfo.third_url,
                    start_t: scriptInfo.start_t
                }}));
                setWsIdCount((prev) => {
                    return prev + 1;
                });
            }
        };

        huiboWs.current.onmessage = e => {
            const res = JSON.parse(e.data);
            if (res.errno === 0 && res.data) {
                const data = res.data;
                if (res.path === 'interact.push') {
                    setCommentList((prev) => {
                        return [
                            ...prev,
                            data
                        ];
                    });
                }
                else if (res.path === 'live.gmv.push') {
                    setGmvData(data);
                }
                else if (res.path === 'interact.good.card') {
                    goodsCardByPlugin(source, {
                        platuserId: cuInfo.third_id,
                        data: {
                            id: data.third_id,
                            name: ''
                        }
                    });
                }
            }
        };

        huiboWs.current.onerror = e => {
            console.log('on error', e);
        };
    };

    const heartbeatWsStart = async () => {
        heartWs.current = new WebSocket(
            `ws://127.0.0.1:${window.huibo_external_plugin_ws_live_port}/ws/heartbeat`
        );

        heartWs.current.onopen = async e => {
            const sign = await getSign();
            heartWs.current.send(JSON.stringify({
                taskType: TASKTYPEMAP[source],
                userId: window.huibo_external_auth_uk,
                accountType: ACCOUNTTYPEMAP[source],
                action: 'HEARTBEAT',
                requestPlat: 'HUI_BO_XING',
                forceLogin: true,
                timeStamp: sign.ts,
                sign
            }));
        };

        heartWs.current.onmessage = e => {
            const res = JSON.parse(e.data);
            if (res.code === 0 && res.action === 'HEARTBEAT') {
                setLiveIsAlive(() => true);
            }
            else {
                setLiveIsAlive(() => false);
                clearWs();
                setCommentList(() => []);
            }
        };

        heartWs.current.onerror = e => {
            setLiveIsAlive(() => false);
            clearWs();
            setCommentList(() => []);
        };
    };

    const pluginWsStart = async () => {
        const liveInfo = store.externalLiveInfo;
        pluginWs.current = new WebSocket(
            `ws://127.0.0.1:${window.huibo_external_plugin_ws_port}/ws/barrage`
        );

        pluginWs.current.onopen = e => {
            pluginWs.current.send(JSON.stringify({
                TaskType: TASKTYPEMAP[source],
                requestPlat: 'HUI_BO_XING',
                UserId: window.huibo_external_auth_uk
            }));
        };

        pluginWs.current.onmessage = e => {
            const res = JSON.parse(e.data);
            if (res.code === 0 && res.action === 'MESSAGE' && res.body && res.body.length) {
                const commentList = res.body.map(c => {
                    return {
                        room_id: liveInfo.room_id,
                        third_room: liveInfo.third_url,
                        user_name: c.info.Nickname,
                        content: c.message,
                        kind: COMMENTKINDMAP.find(k => k.label === c.type).value,
                        ...c
                    };
                });
                huiboWs.current.send(JSON.stringify({id: wsIdCount, path: 'interact.report', data: {
                    ...commentList[commentList.length - 1]
                }}));
                setWsIdCount((prev) => {
                    return prev + 1;
                });
                setCommentList((prev) => {
                    return [
                        ...prev,
                        ...commentList
                    ];
                });
            }
        };
        pluginWs.current.onerror = e => {
            console.log('on error', e);
        };
    };

    const stopLive = async () => {
        const scriptId = store.externalLiveInfo.script_id;
        let {errno} =  await setLivePushStop({
            script_id: scriptId,
            is_error: false,
            reason: 'external live tab click button'
        });
        if (+errno === 0) {
            await store.fetchExternalLiveInfo();
        }
    };

    const renderEmpty = (text) => (
        <div className="digital-external-live-empty">
            <img className="digital-external-live-empty-img" src={iconEmpty} alt="" />
            <div className="digital-external-live-empty-title">{text}</div>
        </div>
    );

    const renderCommentItem = (item, index) => (
        <div className='comment-item' key={`comment-item-${index}`}>
            <div className={`comment-item-container ${item.isAdmin ? 'admin' : ''}`}>
                {item.isAdmin && <span className="comment-item-container-admin">
                    <img src={iconAnchor} />
                </span>}
                <span className="comment-item-container-username">
                    {item.user_name}{item.kind === 1 ? ':' : ''}
                </span>
                {item.reply && <span className="comment-item-container-username">@{item.reply}</span>}
                <span className="comment-item-container-content">{
                    item.kind === 1 ? item.content
                        : item.kind === 2 ? '进入了直播间'
                            : item.kind === 3 ? '点赞了直播间'
                                : item.kind === 4 ? '关注了直播间'
                                    : ''
                }</span>
            </div>
        </div>
    );

    return (
        <div className="digital-external-live">
            {store.externalLiveInfo?.id
                ? <React.Fragment>
                    <div className="digital-external-live-title">
                        <div className="digital-external-live-title-left">
                            <div
                                className="digital-external-live-title-left-live-ing"
                                onClick={() => {
                                    window.open(`https://${window.location.host}/#/externallivepage`, '_blank');
                                }}
                            >
                                直播中
                                <img src={iconPlaying} />
                            </div>
                            <div className="digital-external-live-title-left-live-name">
                                {scriptInfo.script_title}
                            </div>
                        </div>
                        <div className="digital-external-live-title-right">
                            <div className="digital-external-live-title-right-btn" onClick={stopLive}>
                                <img src={iconStopExternalLive} />
                            </div>
                        </div>
                    </div>
                    <div className="digital-external-live-content">
                        <div className="digital-external-live-content-live">
                            <div className="digital-external-live-content-live-frame">
                                <FlvPlayer url={scriptInfo.pull_path} />
                            </div>
                        </div>
                        <div className="digital-external-live-content-comment">
                            <div className="digital-external-live-content-comment-title">
                                <div className="digital-external-live-content-comment-title-name">
                                    用户实时评论
                                </div>
                                <div className="digital-external-live-content-comment-title-refresh" onClick={getWsData}>
                                    <img src={iconRefresh} className='digital-external-live-content-comment-title-refresh-icon' />
                                    获取实时评论
                                </div>
                            </div>

                            <Spin spinning={authLoading} wrapperClassName="digital-external-live-content-comment-list-loading">
                                {authLoading ? <React.Fragment />
                                    : <React.Fragment>
                                        {
                                            hasAuth
                                                ? <div className="digital-external-live-content-comment-list">
                                                    {commentList.length
                                                        ? commentList.map((item, index) => renderCommentItem(item, index))
                                                        : renderEmpty('暂无评论')
                                                    }
                                                </div>
                                                : <div className="no-auth">
                                                    未授权站外账号 无法获取站外商品
                                                    <span className='go-to-auth' onClick={() => store.externalStore.setShowExternalModal(true)}>
                                                        去授权<img className="icon" src={iconRightArrow} alt="" />
                                                    </span>
                                                </div>
                                        }
                                    </React.Fragment>
                                }
                            </Spin>

                        </div>
                        <div className="digital-external-live-content-gmv-config">
                            <div className="digital-external-live-content-gmv">
                                <div className="digital-external-live-content-gmv-title">
                                    <div className="digital-external-live-content-gmv-title-name">
                                        用户实时评论
                                    </div>
                                    <div className="digital-external-live-content-gmv-title-desc">
                                        成交金额和开播时长数据可能会有时延
                                    </div>
                                </div>
                                <div className="digital-external-live-content-gmv-data">
                                    <div className="digital-external-live-content-gmv-data-item">
                                        <div className="digital-external-live-content-gmv-data-item-label">
                                            累计成交金额
                                        </div>
                                        <div className="digital-external-live-content-gmv-data-item-value">
                                            {gmvData.gmv !== undefined ? gmvData.gmv : '--'}
                                        </div>
                                    </div>
                                    <div className="digital-external-live-content-gmv-data-item">
                                        <div className="digital-external-live-content-gmv-data-item-label">
                                            在线人数
                                        </div>
                                        <div className="digital-external-live-content-gmv-data-item-value">
                                            {gmvData.unique_users !== undefined ? gmvData.unique_users : '--'}
                                        </div>
                                    </div>
                                    <div className="digital-external-live-content-gmv-data-item">
                                        <div className="digital-external-live-content-gmv-data-item-label">
                                            开播时长
                                        </div>
                                        <div className="digital-external-live-content-gmv-data-item-value">
                                            {gmvData.live_duration !== undefined ? gmvData.live_duration : '--'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="digital-external-live-content-config">
                                <div className="digital-external-live-content-config-title">
                                    直播设置
                                </div>
                                <div className="digital-external-live-content-config-list">
                                    <Checkbox checked={!!scriptInfo?.need_qa} disabled>开启智能回复，自动回复直播间各类问题</Checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
                : renderEmpty('当前无站外开播')
            }
        </div>
    );
};
export default observer(ExternalLive);